import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Gift } from 'lucide-react';

// Rewards banner for staff flows (booking modal + teller POS).
// Shows points, redeemable rewards and the AI upsell line for the next reward.
export const RewardUpsellBanner = ({ patientId, allowRedeem = false, onRedeemed }) => {
  const [data, setData] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [payMethods, setPayMethods] = useState(null);
  const [payMethodId, setPayMethodId] = useState('');

  useEffect(() => {
    if (!patientId) { setData(null); return; }
    let live = true;
    api.get(`/rewards/next-reward/${patientId}`)
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [patientId]);

  if (!patientId || !data) return null;
  const { available_points, eligible_rewards = [], next_reward } = data;
  if (!next_reward && eligible_rewards.length === 0) return null;

  const redeemNow = async (reward) => {
    if (!window.confirm(`Redeem ${reward.required_points} points for "${reward.reward_name}" now? A ₹0 reward appointment for today will be created.`)) return;
    setRedeeming(true);
    try {
      const res = await api.post('/rewards/redeem', {
        patient_id: patientId,
        reward_id: reward.id,
        redeem_now: true,
        remarks: 'Redeemed at counter',
      });
      toast.success(`Reward redeemed — appointment ${res.data?.appointment?.ref_number || ''} created (₹0)`);
      const fresh = await api.get(`/rewards/next-reward/${patientId}`);
      setData(fresh.data);
      onRedeemed && onRedeemed(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Redemption failed');
    } finally {
      setRedeeming(false);
    }
  };

  const startPayRedeem = async () => {
    try {
      const res = await api.get('/payment-methods');
      const rows = res.data || [];
      setPayMethods(rows);
      setPayMethodId(rows[0]?.id || '');
    } catch (_e) {
      toast.error('Could not load payment methods');
    }
  };

  const confirmPayRedeem = async () => {
    const nr = data?.next_reward;
    if (!nr || !payMethodId) return;
    if (!window.confirm(`Collect ₹${Number(nr.amount_needed).toLocaleString('en-IN')} and redeem "${nr.reward_name}" using the patient's points now?`)) return;
    setRedeeming(true);
    try {
      const res = await api.post('/rewards/redeem-with-payment', {
        patient_id: patientId,
        reward_id: nr.id,
        payment_method_id: payMethodId,
      });
      toast.success(`₹${res.data?.payment?.amount} collected (${res.data?.payment?.receipt_number}) — ${nr.reward_name} redeemed`);
      setPayMethods(null);
      const fresh = await api.get(`/rewards/next-reward/${patientId}`);
      setData(fresh.data);
      onRedeemed && onRedeemed(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Redeem with payment failed');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-3 space-y-2" data-testid="reward-upsell-banner">
      <div className="flex items-center gap-2 text-amber-900 text-sm font-semibold">
        <Gift className="w-4 h-4" />
        Rewards · {Number(available_points).toLocaleString('en-IN')} points
      </div>
      {eligible_rewards.length > 0 && (
        <div className="space-y-1.5" data-testid="reward-banner-eligible">
          {eligible_rewards.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 bg-white/70 border border-emerald-200 rounded-md px-2.5 py-1.5">
              <span className="text-xs text-emerald-900">
                🎁 <strong>{r.reward_name}</strong> ready to redeem ({r.required_points} pts)
                {r.linked_service_name ? ` — ${r.linked_service_name}` : ''}
              </span>
              {allowRedeem && (
                <button
                  onClick={() => redeemNow(r)}
                  disabled={redeeming}
                  className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded px-2.5 py-1 disabled:opacity-50 shrink-0"
                  data-testid={`redeem-reward-${r.id}`}
                >
                  {redeeming ? 'Redeeming…' : 'Redeem'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {next_reward && (
        <div className="text-xs text-amber-900 space-y-1.5" data-testid="reward-banner-next">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span>
              Pay <span className="font-semibold">₹{Number(next_reward.amount_needed).toLocaleString('en-IN')}</span>
              {' '}along with your <span className="font-semibold">{Number(available_points).toLocaleString('en-IN')} points</span> to unlock{' '}
              <span className="font-semibold">{next_reward.reward_name}</span>
            </span>
            {allowRedeem && !payMethods && (
              <button
                onClick={startPayRedeem}
                disabled={redeeming}
                className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded px-2.5 py-1 disabled:opacity-50 shrink-0"
                data-testid="pay-redeem-start-button"
              >
                Redeem with ₹{Number(next_reward.amount_needed).toLocaleString('en-IN')} payment
              </button>
            )}
          </div>
          {allowRedeem && payMethods && (
            <div className="flex items-center gap-2 flex-wrap bg-white/70 border border-amber-200 rounded-md px-2.5 py-1.5">
              <select
                value={payMethodId}
                onChange={(e) => setPayMethodId(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1"
                data-testid="pay-redeem-method-select"
              >
                {payMethods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button
                onClick={confirmPayRedeem}
                disabled={redeeming || !payMethodId}
                className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded px-2.5 py-1 disabled:opacity-50"
                data-testid="pay-redeem-confirm-button"
              >
                {redeeming ? 'Processing…' : `Collect ₹${Number(next_reward.amount_needed).toLocaleString('en-IN')} & Redeem`}
              </button>
              <button onClick={() => setPayMethods(null)} className="text-xs text-gray-500 underline" data-testid="pay-redeem-cancel-button">
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RewardUpsellBanner;
